// =====================================================================
// AsterWallet — shared onboarding helpers.
//
// This module is the SINGLE SOURCE OF TRUTH for onboarding logic that
// must behave identically across mobile (Expo) and web (Vite SPA):
//
//   - PO Box detection (CIP requires residential address)
//   - 18+ check (compliance floor — client is UX only; server decides)
//   - SSN normalisation / validation / masking (never persisted)
//   - Phone → E.164 conversion
//   - Progress state-machine + resume routing (with 24h expiry)
//   - Banned-field retention seam (SSN, tax_id, itin stripped at write)
//   - Error → user copy mapping (deliberately-vague on rejection)
//   - Reason-leak word list
//
// The routes that these decisions dispatch to differ per client
// (mobile uses `/onboarding-*`, web uses `/signup/*`), so route
// resolution takes a client-provided map — see `routeForIdentityStatus`.
//
// EXTRACTION INVARIANT: `tests/test_shared_onboarding_sole_impl.py`
// (backend, grep-based) and `tests/test_shared_onboarding_client.spec.ts`
// (client-side, when added) both check that no duplicate implementation
// of PO Box / 18+ / SSN validation lives elsewhere in the tree. Same
// discipline as the sole-writer invariant on CIP columns.
//
// The `IdentityStatus` type is re-exported here (as a mirror of the
// api-contract shape) so shared code doesn't depend on a client-side
// path. Any drift between this mirror and `frontend/src/api/contract.ts`
// is caught by the client contract test.
// =====================================================================

// ---------------------------------------------------------------------
// IdentityStatus mirror.
//
// Kept in sync with `frontend/src/api/contract.ts::IdentityStatus`
// manually. Shared code cannot import from `@/src/api/contract` because
// the alias only exists in the mobile app, and Vite would need a
// separate resolver hop. Mirror is small enough that a drift is
// caught by a compile error on the client side when they import.
// ---------------------------------------------------------------------

export type IdentityStatus =
  | "unverified"
  | "verifying"
  | "pending"
  | "verified"
  | "action_needed"
  | "rejected"
  | "blocked";

// ---------------------------------------------------------------------
// PO Box detection — CIP requires a residential address. Rejected
// inline at the field, not after submission, so the user can fix it
// without seeing a "submitting…" spinner first.
//
// The regex is intentionally FUZZY — the US Post Office runs a
// service, not a pattern match. Real users type "PO Box", "P.O. Box",
// "POBox", "PMB", "Post Office Box", "PO 123", "PO#5", and mid-line
// hybrids like "123 Main St PO Box 5". We catch all of these.
//
// Design choices (Jun 2026 rewrite):
//   * NOT anchored to start-of-line — a residential address with
//     "PO Box" appended is still a PO Box for CIP purposes.
//   * `\b` word boundary BEFORE the pattern — guards against embedded
//     letter sequences like "Postbox", "Boxwood", "POBoxwood".
//   * `(?![a-z])` lookahead AFTER the box/pmb token — allows a digit
//     ("POBox5") or non-letter ("POBox 5.") but rejects letters
//     ("POBoxwood").
//   * Case-insensitive (`/i`).
//
// Cross-language corpus in
// `/app/shared/onboarding/fixtures/po-box-corpus.json` asserts the
// TS + Python implementations agree on every case. The invariant is
// enforced by:
//   * `/app/shared/onboarding/tests/po-box.test.ts`   (Vitest)
//   * `/app/backend/tests/test_po_box_corpus.py`      (Pytest)
// Both suites read the SAME JSON file. A regex change in ONE side
// that doesn't update the OTHER surfaces as a suite failure on the
// divergent side. Comments claiming synchronisation are not enough.
//
// Cases we DELIBERATELY DO NOT catch (server + manual review handle):
//   * `Box 5` alone (rural-style shorthand — ambiguous with PO Box)
//   * `#5` alone (apartment/unit — false-positive rate too high)
//   * Foreign-language variants of "PO Box"
//
// The server's `_is_po_box_address` in
// /app/backend/product/identity_provider.py uses the equivalent
// Python patterns. Both are proven to agree by the corpus tests.
// ---------------------------------------------------------------------

const PO_BOX_PATTERNS: RegExp[] = [
  // "PO Box" / "P.O. Box" / "POBox" / "POBox5" — the (?![a-z])
  // guards against "POBoxwood" while permitting a digit or non-letter
  // immediately after. Whitespace between P/O/Box is OPTIONAL.
  /\bp\.?\s*o\.?\s*box(?![a-z])/i,
  // Short form: "PO 123" / "P.O.45" / "PO#5" / "P.O.#5" / "PO # 5"
  // — a digit reachable through optional dots, whitespace, and one
  // optional `#`.
  /\bp\.?\s*o\.?\s*#?\s*\d/i,
  // "POB 12" / "POB12" — abbreviated form.
  /\bpob\s*\d/i,
  // "Post Office Box" (spelled out). `(?![a-z])` guards against
  // "Post Office Boxwood" (extraordinarily rare, still handled).
  /\bpost\s+office\s+box(?![a-z])/i,
  // "PMB" (Private Mail Box) — anywhere in the line. `(?![a-z])`
  // guards against embedded matches like "COMPMBox".
  /\bpmb(?![a-z])/i,
];

export function isPoBox(line1: string): boolean {
  const s = line1 ?? "";
  return PO_BOX_PATTERNS.some((rx) => rx.test(s));
}

// ---------------------------------------------------------------------
// 18+ check — the client MUST reject under-18 before submit so the
// terminal copy reads immediately. Server ALSO checks (authoritative);
// this is defence in depth, not the source of truth.
//
// Server enforcement: `product/identity.submit_identity` gates
// under-18 BEFORE dispatching to the provider — see the server-side
// age-gate tests.
//
// Boundary: age is computed at the LOCAL calendar day granularity. A
// user born 18 years ago yesterday is 18. A user born 18 years ago
// tomorrow is not.
// ---------------------------------------------------------------------

/**
 * True when the person born on `dobYyyyMmDd` is at least 18 on `now`
 * in local time. Returns false when the DOB string is malformed.
 */
export function isEighteenPlus(
  dobYyyyMmDd: string,
  now: Date = new Date(),
): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobYyyyMmDd);
  if (!m) return false;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  const dob = new Date(y, mo, d);
  if (Number.isNaN(dob.getTime())) return false;
  const eighteen = new Date(dob);
  eighteen.setFullYear(eighteen.getFullYear() + 18);
  return now.getTime() >= eighteen.getTime();
}

// ---------------------------------------------------------------------
// SSN normalisation. The input arrives with dashes / spaces from the
// user; the wire format is 9 raw digits. The client MUST NOT persist
// this string anywhere — see the onboarding-store retention policy
// (stripBannedFields below).
//
// ITIN is accepted in the same field for users who don't have an SSN.
// Wire format for both is nine digits.
// ---------------------------------------------------------------------

export function normaliseSsn(raw: string): string {
  return (raw ?? "").replace(/\D+/g, "").slice(0, 9);
}

export function isValidSsn(raw: string): boolean {
  const n = normaliseSsn(raw);
  return /^\d{9}$/.test(n);
}

/**
 * Masked display for the field — never shows the digits after the user
 * moves off the field. This is a defence against a shoulder-surf, not
 * against a screenshot; the sole purpose is to keep the numbers out of
 * a casual accessibility-screenshot leak.
 */
export function maskSsn(raw: string): string {
  const n = normaliseSsn(raw);
  if (n.length === 0) return "";
  return "•".repeat(Math.max(0, n.length - 4)) + n.slice(-4);
}

// ---------------------------------------------------------------------
// Phone number → E.164. For US numbers we accept common inputs:
//   (415) 555-1212     -> +14155551212
//   415-555-1212       -> +14155551212
//   4155551212         -> +14155551212
//   +14155551212       -> +14155551212
// International with a leading '+' passes through if the format is
// plausible. Anything else returns null and the field renders an error.
// ---------------------------------------------------------------------

export function toE164(raw: string, defaultCountryCode = "1"): string | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  if (s.startsWith("+")) {
    const digits = s.slice(1).replace(/\D+/g, "");
    if (digits.length >= 7 && digits.length <= 15) return "+" + digits;
    return null;
  }
  const digits = s.replace(/\D+/g, "");
  if (digits.length === 10) return "+" + defaultCountryCode + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  return null;
}

// ---------------------------------------------------------------------
// Progress state machine.
//
// The screen order is fixed and known. Progress is a discriminated
// union so a mid-flow crash resumes exactly where the user left off.
// Every value carries only the fields COMPLETED at that step — the
// current step's inputs are held in local component state, not
// persisted, until the user confirms and advances.
//
// CRITICAL: the SSN is NEVER a member of any progress variant. The
// `stripBannedFields` retention seam below rejects it at write time
// AND the /onboarding-test row 6 fires if it slips through.
// ---------------------------------------------------------------------

export type OnboardingStep =
  | "welcome"
  | "phone"
  | "otp"
  | "passkey"
  | "legal"
  | "name"
  | "dob"
  | "address"
  | "ssn"
  | "verifying"
  | "pending"
  | "documents"
  | "rejected"
  | "blocked"      // terminal — under-18 or sanction; client wipes progress
  | "done";

export interface OnboardingProgress {
  /** Which screen the user should land on when they resume. */
  step: OnboardingStep;
  /** Fixed-instant when this progress was written. Used for 24h expiry. */
  written_at: string; // ISO
  // Persistable payload — every field below is safe to store.
  phone_e164?: string;
  challenge_id?: string; // active OTP challenge, if any
  otp_verified?: boolean;
  /**
   * Short-lived bearer returned by verifyPhoneOtp. Consumed by
   * enrollDevice on the passkey screen. Safe to persist for the 24h
   * onboarding window — the token itself has a 10-min TTL enforced
   * server-side, so a resumed session past that window just gets a
   * fresh OTP.
   */
  verification_token?: string;
  verification_expires_at?: string; // ISO-8601
  passkey_registered?: boolean;
  legal_accepted_at?: string;
  legal_name?: string;
  date_of_birth?: string; // 'YYYY-MM-DD'
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: "US";
  };
  // ssn: DELIBERATELY OMITTED FROM THIS TYPE. If you add it, /onboarding-
  // test row 6 will fail — that's the point.
}

/**
 * Empty starting state. `written_at` is now so the 24h expiry works
 * even for a first-time user who abandons before ever writing.
 */
export function emptyProgress(now: Date = new Date()): OnboardingProgress {
  return { step: "welcome", written_at: now.toISOString() };
}

/**
 * Advance the progress to the given step, folding in any commit fields.
 * The retention seam (setProgress) strips any accidentally-added
 * banned fields at write time.
 */
export function advance(
  cur: OnboardingProgress,
  next: OnboardingStep,
  commit: Partial<OnboardingProgress> = {},
  now: Date = new Date(),
): OnboardingProgress {
  return {
    ...cur,
    ...commit,
    step: next,
    written_at: now.toISOString(),
  };
}

/**
 * CIP steps — everything AFTER the phone was proven reachable. Landing
 * on any of these without a valid OTP-verified prereq is an
 * authentication bypass (someone picks up an abandoned session on a
 * shared device and completes onboarding as that person). Resume MUST
 * bounce back to /onboarding-phone in that case, not into the CIP
 * screens.
 *
 * `passkey` sits at the boundary — it's the screen right after OTP —
 * so we include it in the guarded set. If a device restart lands there
 * without otp_verified, we re-run OTP.
 */
const CIP_STEPS: ReadonlySet<OnboardingStep> = new Set<OnboardingStep>([
  "passkey",
  "legal",
  "name",
  "dob",
  "address",
  "ssn",
  "verifying",
  "pending",
  "documents",
  "rejected",
  "blocked",
  "done",
]);

/**
 * Given a persisted progress, decide which route the user should land
 * on. If the progress has expired (>24h) OR the progress is malformed,
 * returns "welcome".
 *
 * Resume guard: if the persisted step is a CIP step but `otp_verified`
 * is not true (missing / false), we route to "phone" so the user
 * re-proves phone reachability before we let them back into the
 * identity screens. A truly missing phone_e164 falls all the way back
 * to "welcome".
 *
 * `blocked` is terminal — the user cannot progress past it. The
 * caller is expected to clear the persisted progress before landing
 * on the blocked screen, so this function returning "blocked" indicates
 * a bug in the caller. It still returns the correct route for
 * defence in depth.
 */
export function routeForProgress(
  p: OnboardingProgress | null,
  now: Date = new Date(),
): OnboardingStep {
  if (!p) return "welcome";
  const written = new Date(p.written_at).getTime();
  if (Number.isNaN(written)) return "welcome";
  const ageMs = now.getTime() - written;
  if (ageMs > 24 * 60 * 60 * 1000) return "welcome";
  // Resume guard for CIP steps — see CIP_STEPS above.
  if (CIP_STEPS.has(p.step) && p.otp_verified !== true) {
    return p.phone_e164 ? "phone" : "welcome";
  }
  return p.step;
}

// ---------------------------------------------------------------------
// The banned-field allowlist / rejection list for the retention seam.
// Adding to BANNED_FIELDS is a compliance action — do it if you
// discover another CIP field that must never sit on disk.
// ---------------------------------------------------------------------

export const BANNED_PROGRESS_FIELDS = [
  "ssn", "ssn_last4", "tax_id", "itin",
] as const;

/**
 * Runtime check: strip any banned key from a payload before persistence.
 * Returns the sanitised payload plus the list of keys stripped so the
 * caller can log a warning at development time (never in production).
 */
export function stripBannedFields(
  payload: Record<string, unknown>,
): { safe: Record<string, unknown>; stripped: string[] } {
  const safe: Record<string, unknown> = {};
  const stripped: string[] = [];
  const bannedSet = new Set<string>(BANNED_PROGRESS_FIELDS);
  for (const [k, v] of Object.entries(payload)) {
    if (bannedSet.has(k)) {
      stripped.push(k);
    } else {
      safe[k] = v;
    }
  }
  return { safe, stripped };
}

// ---------------------------------------------------------------------
// Error copy mapping for submitOnboardingIdentity failures.
//
// Copy sheet rules (Pass B):
//   - No connection / unknown error → "Can't reach Aster right now."
//   - Server 5xx (internal_error)   → "Something went wrong on our end."
//   - Unauthenticated               → session-ended language
//   - Rate limited                  → "Too many attempts."
//   - Validation failed             → server's field message (safe to
//                                     display; contract guarantees).
//   - Anything else (including
//     identity_verification_required
//     and rejected outcomes)        → the DELIBERATELY-VAGUE line:
//                                     "We couldn't verify your identity
//                                     with the information provided.
//                                     Our support team can help — get
//                                     in touch." Never states a reason.
//                                     Stating the reason gives fraudsters
//                                     a tuning signal. Legitimate users
//                                     are routed to support.
// ---------------------------------------------------------------------

/**
 * Minimal shape we need to map an error to copy. The real `AsterApiError`
 * satisfies this; the helper accepts a raw shape so tests can build
 * fixtures without instantiating the class.
 */
export interface OnboardingErrorLike {
  code: string;
  message?: string;
}

const REJECTED_COPY =
  "We couldn't verify your identity with the information provided. " +
  "Our support team can help — get in touch.";
const CONNECTION_COPY =
  "Can't reach Aster right now. Check your connection and try again.";
const SERVER_COPY = "Something went wrong on our end. Try again in a moment.";
const SESSION_COPY = "Your session ended. Sign in again to continue.";
const RATE_COPY = "Too many attempts. Give it a moment and try again.";

export function onboardingErrorCopy(
  err: OnboardingErrorLike | null | undefined,
): string {
  if (!err || !err.code) return CONNECTION_COPY;
  switch (err.code) {
    case "validation_failed":
      // Server-provided message; contract guarantees display-safe.
      return err.message ?? "Check the details and try again.";
    case "rate_limited":
      return RATE_COPY;
    case "internal_error":
      return SERVER_COPY;
    case "unauthenticated":
      return SESSION_COPY;
    default:
      // identity_verification_required and any other terminal / unusual
      // code — never state a reason.
      return REJECTED_COPY;
  }
}

/**
 * True when the copy for the given error is the deliberately-vague
 * "rejected" line. Used by /onboarding-test row 13 to assert that
 * rejected copy contains no reason.
 */
export function isRejectedCopy(s: string): boolean {
  return s === REJECTED_COPY;
}

/** Exposed for testability of the exact copy constants. */
export const ONBOARDING_ERROR_COPY = {
  REJECTED: REJECTED_COPY,
  CONNECTION: CONNECTION_COPY,
  SERVER: SERVER_COPY,
  SESSION: SESSION_COPY,
  RATE: RATE_COPY,
} as const;

/**
 * Words that would leak a rejection REASON to the user. If any of these
 * appears in the rejected copy, we're giving fraudsters a tuning signal.
 * /onboarding-test row 13 asserts none appear.
 */
export const REASON_LEAK_WORDS: readonly string[] = [
  "ssn",
  "social security",
  "address",
  "date of birth",
  "dob",
  "name",
  "match",
  "mismatch",
  "watchlist",
  "sanction",
  "blocked",
  "denied",
  "failed",
  "wrong",
  "incorrect",
  "invalid",
] as const;

// ---------------------------------------------------------------------
// Post-submission routing.
//
// The client MUST route to different destinations for `action_needed`
// (documents will resolve — soft fail) and `rejected` (terminal — the
// user cannot resolve it, only support can). Sanctions and watchlist
// hits fall into `rejected`; sending those users through a document
// upload path (a) wastes their time on a flow that cannot succeed, and
// (b) leaks information about the rejection reason via the retry-and-
// fail-again signal.
//
// The two branches MUST be structurally and tonally indistinguishable
// from the user's perspective at the point of routing — same register,
// no leaked reason, no hint of a difference existing.
//
// The ROUTE STRINGS differ per client (mobile: `/onboarding-*`; web:
// `/signup/*`), so the caller passes a client-specific `routeMap` and
// this helper does the lookup. Same input contract, same enum coverage,
// different concrete destinations.
// ---------------------------------------------------------------------

/**
 * The set of statuses that trigger a client-side route. Every
 * `IdentityStatus` must appear as a key in the client's route map,
 * so a compile error catches a missing branch.
 */
export type IdentityStatusRouteMap = {
  readonly [K in IdentityStatus]: string;
};

/**
 * Look up the route for `status` in the client-provided `routeMap`.
 * The generic parameter preserves the exact concrete route strings so
 * callers get typed navigation (e.g., Expo Router's `Href` shape).
 */
export function routeForIdentityStatus<M extends IdentityStatusRouteMap>(
  status: IdentityStatus,
  routeMap: M,
): M[keyof M] {
  return routeMap[status] as M[keyof M];
}
